import { DestinationEvent } from '@eventbrite/event-renderer';
import { UseQueryOptions } from '@tanstack/react-query';

export interface TopSuggestionsShape {
    slug?: string;
    placeType?: string;
    currentPlace?: string;
    currentPlaceParent?: string;
    latitude?: number;
    longitude?: number;
    value?: string;
    content?: string;
    secondaryContent?: string;
}

export interface bboxShape {
    nw?: LatLong;
    se?: LatLong;
    ne?: LatLong;
    sw?: LatLong;
}

export type LatLong = {
    lng: number;
    lat: number;
};

export interface Location {
    bbox?: bboxShape;
    placeId?: string | null;
    isOnline: boolean;
    longitude: number;
    latitude: number;
    suggestions?: any[];
    slug?: string;
    currentPlace?: string;
    currentPlaceParent?: string;
    country?: string;
    placeType?: string;
    isWaitingForLocation?: boolean;
    isUsingCurrentLocation?: boolean;
    locationDenied?: boolean;
    topSuggestions?: TopSuggestionsShape[];
}

export enum DateTypeEnum {
    ALL = 'all',
    PAST = 'past',
    CURRENT_FUTURE = 'current_future',
    TODAY = 'today',
    TOMORROW = 'tomorrow',
    THIS_WEEK = 'this_week',
    THIS_MONTH = 'this_month',
    THIS_WEEKEND = 'this_weekend',
    THIS_4_WEEKS = 'this_4_weeks',
    NEXT_MONTH = 'next_month',
}

export type DateType =
    | 'all'
    | 'past'
    | 'current_future'
    | 'today'
    | 'tomorrow'
    | 'this_week'
    | 'this_month'
    | 'this_weekend'
    | 'this_4_weeks'
    | 'next_month';

export interface DateAggResponse {
    inflated: {
        from: string;
        display_name: string;
        localized: {
            display_name: string;
        };
        key: string;
        to: string;
    };
    display_name: string;
    key: string;
    doc_count: number;
}

export interface TagAggApiResponse {
    inflated?: {
        prefix: string;
        tag: string;
        display_name: string;
    };
    display_name: string;
    key: string;
    doc_count?: number;
}

export interface OrganizerAutocompleteAggApiResponse {
    inflated: string;
    display_name: string;
    key: string;
    doc_count: number;
}

export type AggType =
    | 'all'
    | {
          dates?: any;
          tags?: any;
          organizertagsautocomplete_agg?: any;
          master_tags?: any;
      };

export type PriceType = 'paid' | 'free' | '';

export interface DateRange {
    from: string;
    to: string;
}

export type SortType = 'default' | 'date' | 'date_and_time' | 'id' | 'quality';

export interface OrganicEventSearchFiltersApiPayload {
    dates?: DateType | DateType[];
    tags: string[];
    price?: PriceType;
    q?: string;
    places?: string[];
    bbox?:
        | string
        | {
              sw: { lng: number; lat: number };
              ne: { lng: number; lat: number };
          };
    currencies?: any;
    dedup?: boolean;
    date_range?: DateRange;
    hash?: string;
    languages?: any;
    online_events_only?: boolean;
    organizations_or?: string[];
    collections_or?: string[];
    include_mature_events?: boolean;
    include_manual_events?: boolean;
    page?: number;
    page_size?: number;
    search_id?: string;
    source?: any;
    special?: string[];
    point_radius?: any;
    client_timezone?: any;
    duration_range?: any;
    start_hour_range?: { start_hour: number; end_hour: number };
    aggs?: AggType;
    continuation?: string;
    sort?: SortType;
    exclude?: {
        ids?: string[];
    };
    image?: boolean;
    page_type: 'POI' | 'TTD' | '';
}

export interface OrganicEventSearchApiResponse {
    articles: {};
    current_user_id: string;
    profile_search: {};
    article_search: {};
    profiles: {};
    events: {
        pagination: {
            object_count: number;
            page_count: number;
            page_number: number;
            page_size: number;
            continuation: string;
        };
        results: DestinationEvent[];
        aggs?: {
            dates?: { buckets: DateAggResponse[] };
            tags?: { buckets: TagAggApiResponse[] };
            organizertagsautocomplete_agg?: {
                buckets: OrganizerAutocompleteAggApiResponse[];
            };
        };
        promoted_results: DestinationEvent[];
        top_match: DestinationEvent[];
    };
    is_staff: boolean;
    search_id: string;
    es: [];
    event_search: OrganicEventSearchFiltersApiPayload;
}

export interface BaseEventsQueryOptions extends UseQueryOptions {
    queryOverload: OrganicEventSearchFiltersApiPayload;
    location: Location;
}

export interface OrganicEventSearchApiPayload {
    browse_surface: 'POI' | 'TTD' | 'search';
    event_search: Omit<OrganicEventSearchFiltersApiPayload, 'page_type'>;
    'expand.destination_event'?: string[];
    'expand.destination_profile'?: string[];
}

type ExpansionBody = {
    'expand.destination_event'?: string[];
    'expand.destination_profile'?: string[];
};

export const EXPANSIONS: ExpansionBody = {
    'expand.destination_event': [
        'primary_venue',
        'image',
        'ticket_availability',
        'saves',
        'event_sales_status',
        'primary_organizer',
        'public_collections',
    ],
};

export type SearchLocationResponse = {
    online_events_only: boolean;
    places?: string[] | undefined;
    point_radius:
        | {
              latitude: number;
              longitude: number;
              radius: '3.5mi' | '1.5mi';
          }
        | undefined;
};
